import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { MIGRATION_STEPS, type ConfigCopyReport, type MigrationDTO, type StageDTO } from '@devrobot/shared';
import { ArrowRightLeft, Ban, CircleAlert, CircleCheck, CircleHelp, Unlock } from 'lucide-react';
import { ApiError } from '../../api/client';
import { abandonMigration, cancelMigration, qk, useApiMutation, useChildJobs, useJob, useMigration } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorAlert } from '../../components/ErrorAlert';
import { JobStages } from '../../components/JobStages';
import { JobsTable } from '../../components/JobsTable';
import { KeyValue } from '../../components/KeyValue';
import { LogViewer } from '../../components/LogViewer';
import { IconChip, PageHeader, Section } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { PageSkeleton } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toaster';
import { haptic } from '../../kit/native';
import { formatBytes } from '../../lib/format';
import NotFoundPage from '../NotFoundPage';
import { useJobStream } from '../jobs/useJobStream';
import { KindBadge, PlanSummary, SourceToDestination } from './PlanSummary';

const HEAD = 'text-xs tracking-wide text-base-content/50 uppercase';

/** Stages from the migration record, for when the parent job is not available. */
function stagesFromMigration(m: MigrationDTO): StageDTO[] {
  const planned = new Map(m.plan.steps.map((s) => [s.name, s]));
  return MIGRATION_STEPS.filter((name) => planned.has(name) || m.steps[name]).map((name) => {
    const rec = m.steps[name];
    return { name, state: rec?.state ?? (planned.get(name)?.skipped ? 'skipped' : 'pending'), finishedAt: rec?.at, message: rec?.message ?? planned.get(name)?.description };
  });
}

function JobPanels({ jobId, migration }: { jobId: string; migration: MigrationDTO }) {
  const job = useJob(jobId);
  const { logs, status } = useJobStream(jobId);
  const stages = job.data?.stages.length ? job.data.stages : stagesFromMigration(migration);
  const terminal = !['pending', 'running'].includes(migration.state);
  const nextSteps = job.data?.result.nextSteps ?? [];
  return (
    <>
      {nextSteps.length > 0 ? (
        <Section title="What to do next">
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {nextSteps.map((s, i) => (
              <li key={i} className="break-anywhere">
                {s}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Stages" actions={<Link className="link text-sm" to={`/jobs/${jobId}`}>Open job</Link>}>
        <ErrorAlert error={job.error} />
        <JobStages stages={stages} />
      </Section>
      <Section title="Logs" description={status === 'streaming' && !terminal ? 'Streaming live.' : status === 'polling' ? 'The live stream is unavailable; refreshing every 3 seconds.' : undefined}>
        <LogViewer
          busy={status === 'streaming' && !terminal}
          lines={logs.map((l) => ({ key: l.seq, ts: l.ts, level: l.level, source: l.stage, message: l.message }))}
          emptyText={terminal ? 'This migration produced no log output.' : 'Waiting for output…'}
        />
      </Section>
    </>
  );
}

function ConfigReport({ r }: { r: ConfigCopyReport }) {
  const groups: [string, string[]][] = [
    ['Copied', r.copied],
    ['Kept at destination', r.skippedExisting],
    ['Overwritten', r.overwritten],
    ['Remapped', r.remapped],
    ['Files', r.files],
  ];
  return (
    <div className="space-y-3 text-sm">
      {groups.map(([label, items]) => (
        <div key={label}>
          <p className="font-medium">
            {label} <span className="font-normal text-base-content/55">({items.length})</span>
          </p>
          {items.length ? (
            <ul className="mt-1 flex flex-wrap gap-1">
              {items.map((n) => (
                <li key={n} className="badge badge-ghost badge-sm font-mono">
                  {n}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
      {r.credentials.length ? (
        <div>
          <p className="font-medium">Credentials</p>
          <ul className="mt-1 space-y-0.5">
            {r.credentials.map((c) => (
              <li key={c.variable}>
                <code className="font-mono">{c.variable}</code>: {c.action.replace('-', ' ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ResultAlert({ m }: { m: MigrationDTO }) {
  if (m.state === 'succeeded')
    return (
      <div role="status" className="alert alert-success alert-soft">
        <CircleCheck className="size-5 shrink-0" aria-hidden="true" />
        <span>The migration succeeded.</span>
      </div>
    );
  if (m.state === 'failed' || m.state === 'canceled' || m.state === 'unknown') {
    const Icon = m.state === 'unknown' ? CircleHelp : m.state === 'canceled' ? Ban : CircleAlert;
    return (
      <div role={m.state === 'failed' ? 'alert' : 'status'} className={`alert alert-soft items-start ${m.state === 'failed' ? 'alert-error' : 'alert-warning'}`}>
        <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1 text-sm">
          <p className="font-semibold">{m.state === 'failed' ? 'The migration failed' : m.state === 'canceled' ? 'The migration was canceled' : 'The outcome is not confirmed'}</p>
          {m.error ? <p className="break-anywhere">{m.error}</p> : null}
          {m.sourceStopped ? <p>The source application is currently stopped.</p> : null}
          {m.state === 'unknown' ? <p>Both targets stay locked. Check the destination, then abandon the migration to release the lock (the source is started again if it was stopped).</p> : null}
        </div>
      </div>
    );
  }
  return null;
}

export default function MigrationDetailPage() {
  const { id = '' } = useParams();
  const q = useMigration(id);
  const children = useChildJobs(q.data?.jobId);
  const toast = useToast();
  const [dialog, setDialog] = useState<'cancel' | 'abandon' | null>(null);
  const invalidate = [qk.migration(id), ['migrations'], ['jobs']];
  const cancel = useApiMutation(() => cancelMigration(id), { invalidate });
  const abandon = useApiMutation(() => abandonMigration(id), { invalidate });

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  }
  const m = q.data;
  const running = m.state === 'pending' || m.state === 'running';
  const canAbandon = m.state === 'unknown' || (m.state === 'failed' && m.sourceStopped);
  const childJobs = children.data ?? m.childJobs;
  const o = m.options;

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Migrations', to: '/migrations' }, { label: <span className="font-mono">{m.id}</span> }]}
        icon={<IconChip icon={ArrowRightLeft} tone="primary" size="lg" />}
        title={<SourceToDestination source={m.source} destination={m.destination} />}
        badges={<StatusBadge kind="migration" value={m.state} size="md" />}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <KindBadge kind={m.kind} />
            <Link className="link link-hover" to={`/projects/${m.project.key}`}>
              {m.project.name}
            </Link>
            <span>started by {m.createdBy}</span>
            <RelativeTime value={m.createdAt} />
          </span>
        }
        actions={
          <>
            {running ? (
              <button type="button" className="btn btn-ghost btn-sm dr-ink-warning" onClick={() => setDialog('cancel')}>
                <Ban className="size-4" aria-hidden="true" /> Cancel migration
              </button>
            ) : null}
            {canAbandon ? (
              <button type="button" className="btn btn-soft btn-warning btn-sm" onClick={() => setDialog('abandon')}>
                <Unlock className="size-4" aria-hidden="true" /> Abandon
              </button>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <ResultAlert m={m} />
          {m.jobId ? (
            <JobPanels jobId={m.jobId} migration={m} />
          ) : (
            <Section title="Stages">
              <JobStages stages={stagesFromMigration(m)} />
            </Section>
          )}
          <Section title="Transfer jobs" description="Agent work on the source and destination VPS.">
            <ErrorAlert error={children.error} />
            <JobsTable jobs={childJobs} emptyTitle="No transfer jobs yet" emptyText="They appear once data starts moving." />
          </Section>
          <Section title="Plan">
            <PlanSummary plan={m.plan} selection={{ databases: o.include.databases, volumes: o.include.volumes, variables: o.include.config.variables, files: o.include.config.files }} showMessages={false} />
          </Section>
        </div>
        <div className="min-w-0 space-y-6">
          <Section title="Summary">
            <KeyValue
              items={[
                { label: 'Project', value: <Link className="link" to={`/projects/${m.project.key}`}>{m.project.name}</Link> },
                { label: 'Source', value: <Link className="link" to={`/targets/${m.source.targetId}`}>{m.source.environment} on {m.source.vps.name}</Link> },
                {
                  label: 'Destination',
                  value: m.destination.targetId ? (
                    <Link className="link" to={`/targets/${m.destination.targetId}`}>
                      {m.destination.environment} on {m.destination.vps.name}
                    </Link>
                  ) : (
                    `${m.destination.environment} on ${m.destination.vps.name}`
                  ),
                },
                { label: 'Consistency', value: o.consistency === 'stop-source' ? 'stop the source while copying' : 'keep the source running' },
                { label: 'Cutover', value: o.cutover ? 'move hostnames and stop the source' : 'no', hidden: m.kind !== 'server-move' },
                { label: 'Overwrite destination', value: o.overwriteDestination ? 'yes' : 'no' },
                { label: 'Source stopped', value: m.sourceStopped ? 'yes' : 'no' },
                { label: 'Created', value: <RelativeTime value={m.createdAt} /> },
                { label: 'Finished', value: <RelativeTime value={m.finishedAt} fallback="not finished" /> },
              ]}
            />
          </Section>
          <Section title="Backups used">
            {[...m.sourceBackupIds, ...m.destBackupIds].length === 0 ? (
              <p className="text-sm text-base-content/60">No backups were required.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {m.sourceBackupIds.map((b) => (
                  <li key={b}>
                    <span className="badge badge-soft badge-info badge-xs mr-1.5">source</span>
                    <Link className="link font-mono text-xs" to={`/targets/${m.source.targetId}?tab=backups`}>
                      {b}
                    </Link>
                  </li>
                ))}
                {m.destBackupIds.map((b) => (
                  <li key={b}>
                    <span className="badge badge-soft badge-warning badge-xs mr-1.5">destination</span>
                    {m.destination.targetId ? (
                      <Link className="link font-mono text-xs" to={`/targets/${m.destination.targetId}?tab=backups`}>
                        {b}
                      </Link>
                    ) : (
                      <span className="font-mono text-xs">{b}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
          {m.configReport ? (
            <Section title="Configuration copied">
              <ConfigReport r={m.configReport} />
            </Section>
          ) : null}
          <Section title="Artifacts" description="Temporary transfer files stored by the control plane.">
            {m.artifacts.length === 0 ? (
              <p className="text-sm text-base-content/60">No artifacts yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-field border border-hairline">
                <table className="table table-sm">
                  <thead>
                    <tr className={HEAD}>
                      <th>Artifact</th>
                      <th>State</th>
                      <th>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.artifacts.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className="font-mono text-xs break-anywhere">{a.name}</span>
                          <div className="text-xs text-base-content/55">
                            {a.kind}
                            {a.sha256 ? (
                              <span className="font-mono" title={a.sha256}>
                                {' '}
                                · {a.sha256.slice(0, 12)}…
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <StatusBadge kind="artifact" value={a.state} size="xs" />
                        </td>
                        <td className="text-sm whitespace-nowrap tabular-nums">{formatBytes(a.sizeBytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      </div>

      {dialog === 'cancel' ? (
        <ConfirmDialog
          title="Cancel this migration?"
          confirmLabel="Request cancellation"
          tone="warning"
          busy={cancel.isPending}
          error={cancel.error}
          onCancel={() => (setDialog(null), cancel.reset())}
          onConfirm={() =>
            cancel.mutate(undefined, {
              onSuccess: () => {
                setDialog(null);
                toast.info('Cancellation requested', { description: 'The migration stops at the next safe point.' });
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p className="text-sm">DevRobot stops the migration at the next safe point and starts the source again if it was stopped.</p>
          <div role="note" className="alert alert-warning alert-soft text-sm">
            Cancellation does not roll back. Data or configuration already copied to the destination stays there.
          </div>
        </ConfirmDialog>
      ) : null}
      {dialog === 'abandon' ? (
        <ConfirmDialog
          title="Abandon this migration?"
          confirmLabel="Abandon and release the lock"
          tone="warning"
          busy={abandon.isPending}
          error={abandon.error}
          onCancel={() => (setDialog(null), abandon.reset())}
          onConfirm={() =>
            abandon.mutate(undefined, {
              onSuccess: () => {
                setDialog(null);
                toast.info('Migration abandoned', { description: 'Both targets are unlocked.' });
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p className="text-sm">
            The migration is marked as abandoned and both targets can be deployed and migrated again. {m.sourceStopped ? 'The source application is started again. ' : ''}Nothing is rolled back: check the destination for partially copied data.
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
